/* eslint-disable no-nested-ternary */
import React, { useContext, useState, useEffect } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
  GlobalContext,
  SET_AG_GRID_COLUMN_STATE,
  SET_AG_GRID_SELECTED_ROWS,
} from '@mummssoftware/global-context';
import { AgGridTableWrapper } from '@mummssoftware/common-ui';
import { editCell, print } from '@mummssoftware/utils/constants';
import { Context } from '../../context';
import { EditCellRenderer, CreatedByCell } from './CellRenderer';
import { smallColumnTableDefs, expandedColumnTableDefs } from './columnDefs';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

const frameworkComponents = {
  editCellRenderer: EditCellRenderer,
  createdByCell: CreatedByCell,
};

export const CompletedFormsList = () => {
  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const {
    smallWidget,
    startLoadingTime,
    getCompletedForms,
    onWidgetReady,
    agency,
    patient,
    formsListLoading,
  } = useContext(Context);
  const {
    completedPatientForms,
    inProgressPatientForms,
    numberForms,
    agGridColumnState,
    agGridSelectedRows,
  } = useContext(GlobalContext);
  const globalContext = useContext(GlobalContext);
  const formdata = inProgressPatientForms.length
    ? inProgressPatientForms
    : completedPatientForms;

  useEffect(() => {
    if (completedPatientForms) {
      const loadingDuration = (Date.now() - startLoadingTime) / 1000;

      onWidgetReady && onWidgetReady('CompletedForms' as mumms.ComponentName);
      console.log('## COMPLETED FORMS WIDGET END ##', loadingDuration, 's');
    }
  }, [completedPatientForms]);

  useEffect(() => {
    if (numberForms && completedPatientForms.length) {
      getCompletedForms(patient?.patientNumber as string, agency);
    }
  }, [numberForms]);

  const columnTableDefs = smallWidget
    ? smallColumnTableDefs
    : expandedColumnTableDefs;

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [gridColumnApi, setGridColumnApi] = useState<any | null>(null);

  const gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
      filter: true,
      sortable: true,
    },
    // Multiple Rows selection
    columnDefs: columnTableDefs,
    suppressRowClickSelection: smallWidget,
    rowSelection: 'multiple',
    rowMultiSelectWithClick: true,
    onGridReady: (params: any) => {
      const gridAPI: GridApi = params.api;
      setGridApi(gridAPI);
      setGridColumnApi(params.columnApi);
      gridAPI.sizeColumnsToFit();
    },
    // change in ag grid state
    onSortChanged: (params: any) => {
      const { columnApi } = params;
      // capture change to sorting in the column headers
      const colState = columnApi.getColumnState();
      globalContext.dispatch({
        type: SET_AG_GRID_COLUMN_STATE,
        payload: colState,
      });
    },
    onSelectionChanged: (params: any) => {
      const gridAPI: GridApi = params.api;
      // Returns a list of all the selected row nodes, regardless of filtering
      const selectedRows = gridAPI.getSelectedRows();
      globalContext.dispatch({
        type: SET_AG_GRID_SELECTED_ROWS,
        payload: selectedRows,
      });
    },
    // Edit/print columns: keep row from staying selected when opening the menu.
    // Primary: CellRenderer sets ag-grid's __ag_Grid_Stop_Propagation on the native click so
    // the row handler never selects (no flash). This is a fallback if that path ever misses.
    // onRowClicked does not run before selection — same ordering issue as onCellClicked.
    onCellClicked: (e) => {
      if (
        (e.column as any).colId === editCell ||
        (e.column as any).colId === print
      ) {
        e.node.setSelected(false);
      }
    },
  };

  useEffect(() => {
    // remember previous AG GRID column state
    if (gridColumnApi && agGridColumnState) {
      gridColumnApi.applyColumnState({
        state: agGridColumnState,
        applyOrder: true,
      });
    }
  }, [gridColumnApi]);

  useEffect(
    () =>
      // reset ag row selected when leaving the page
      () => {
        globalContext.dispatch({
          type: SET_AG_GRID_SELECTED_ROWS,
          payload: null,
        });
      },
    [],
  );

  useEffect(() => {
    // reset node after widget header printing
    if (!agGridSelectedRows && gridApi) {
      gridApi.deselectAll();
    }
  }, [agGridSelectedRows]);

  return (
    <Box position="relative" width="100%" height="100%">
      {!!formsListLoading && (
        <>
          <LinearProgress
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
            }}
            color="primary"
          />
          <Box
            role="status"
            aria-busy
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              pointerEvents: 'auto',
            }}
          />
        </>
      )}
      <AgGridTableWrapper
        id="completed-forms-wrapper"
        className={`${gridTheme.className} light`}
      >
        <AgGridReact
          gridOptions={gridOptions}
          rowData={formdata || []}
          rowHeight={gridTheme.rowHeight}
          headerHeight={gridTheme.headerHeight}
          frameworkComponents={frameworkComponents}
        />
      </AgGridTableWrapper>
    </Box>
  );
};
